import React, { useState, useRef, useEffect } from 'react';

const FeaturesShowcase = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const features = [
    {
      id: 'contacts',
      title: 'Gestion de Contacts',
      description: 'Centralisez tous vos candidats avec leurs CV, notes, tags et processus de recrutement. Importez automatiquement les profils LinkedIn.',
      icon: '👥',
      color: 'from-blue-500 to-purple-600',
      stats: [
        { label: 'Profils Stockés', value: 'Illimité' },
        { label: 'Import CV', value: 'Auto' },
        { label: 'Tags', value: 'Perso' }
      ],
      mockup: 'contacts'
    },
    {
      id: 'kanban',
      title: 'Processus Kanban',
      description: 'Suivez vos candidats à travers les étapes de recrutement avec un tableau Kanban intuitif. Drag & drop pour changer les statuts.',
      icon: '📊',
      color: 'from-green-500 to-teal-600',
      stats: [
        { label: 'Étapes', value: '10' },
        { label: 'Mise à Jour', value: 'Temps Réel' },
        { label: 'Suivi', value: 'Visuel' }
      ],
      mockup: 'kanban'
    },
    {
      id: 'clients',
      title: 'Gestion Clients',
      description: 'Organisez vos partenaires clients, leurs postes ouverts et les contrats. Associez facilement vos contacts aux entreprises.',
      icon: '🏢',
      color: 'from-purple-500 to-pink-600',
      stats: [
        { label: 'Clients', value: 'Illimité' },
        { label: 'Postes', value: 'Multi' },
        { label: 'Contrats', value: 'PDF' }
      ],
      mockup: 'clients'
    },
    {
      id: 'emails',
      title: 'Templates Email',
      description: 'Créez et utilisez des modèles d\'emails personnalisés avec des variables dynamiques. Envoyez des emails groupés facilement.',
      icon: '📧',
      color: 'from-orange-500 to-red-600',
      stats: [
        { label: 'Templates', value: 'Perso' },
        { label: 'Variables', value: 'Auto' },
        { label: 'Envoi', value: 'Groupé' }
      ],
      mockup: 'emails'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const renderMockup = (type) => {
    switch (type) {
      case 'contacts':
        return (
          <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 h-80">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold">Base de Contacts</h3>
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
            
            {/* Contact List */}
            <div className="space-y-3">
              {[
                { name: 'Marie Dupont', company: 'Tech Corp', tags: ['React', 'Senior'] },
                { name: 'Jean Martin', company: 'StartUp XYZ', tags: ['Node.js', 'Junior'] },
                { name: 'Sarah Johnson', company: 'Big Corp', tags: ['Manager', 'Paris'] }
              ].map((contact, i) => (
                <div key={i} className="bg-slate-700/50 rounded-lg p-3 flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                    {contact.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-medium">{contact.name}</div>
                    <div className="text-slate-300 text-sm">{contact.company}</div>
                  </div>
                  <div className="flex space-x-1">
                    {contact.tags.map((tag, j) => (
                      <span key={j} className="px-2 py-1 bg-blue-600/30 text-blue-200 text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'kanban':
        return (
          <div className="relative bg-gradient-to-br from-emerald-900 to-teal-900 rounded-2xl p-6 h-80">
            <div className="text-white font-semibold mb-4">Processus de Recrutement</div>
            
            {/* Kanban columns */}
            <div className="grid grid-cols-4 gap-2 h-64">
              {['Shortlist', 'Entretien', 'Offre', 'Validé'].map((column, i) => (
                <div key={i} className="bg-emerald-800/30 rounded-lg p-2">
                  <div className="text-emerald-200 text-xs font-medium mb-2 text-center">{column}</div>
                  <div className="space-y-2">
                    {i === 0 && (
                      <>
                        <div className="bg-white/20 rounded p-2 text-white text-xs">Marie D.</div>
                        <div className="bg-white/20 rounded p-2 text-white text-xs">Jean M.</div>
                      </>
                    )}
                    {i === 1 && (
                      <div className="bg-white/20 rounded p-2 text-white text-xs">Sarah J.</div>
                    )}
                    {i === 2 && (
                      <div className="bg-white/20 rounded p-2 text-white text-xs">Paul L.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Live indicator */}
            <div className="absolute bottom-4 right-4 flex items-center text-green-400 text-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-ping mr-2"></div>
              Temps réel
            </div>
          </div>
        );

      case 'clients':
        return (
          <div className="relative bg-gradient-to-br from-purple-900 to-pink-900 rounded-2xl p-6 h-80">
            <div className="text-white font-semibold mb-4">Gestion Clients</div>
            
            {/* Client cards */}
            <div className="space-y-3">
              {[
                { name: 'TechCorp', posts: 3, contract: true, manager: 'Julie' },
                { name: 'StartUp Innovation', posts: 1, contract: false, manager: 'Marc' },
                { name: 'Big Enterprise', posts: 5, contract: true, manager: 'Sophie' }
              ].map((client, i) => (
                <div key={i} className="bg-purple-800/30 rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-white font-medium">{client.name}</div>
                      <div className="text-purple-200 text-sm">{client.posts} postes ouverts</div>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs ${
                      client.contract ? 'bg-green-600/30 text-green-200' : 'bg-red-600/30 text-red-200'
                    }`}>
                      {client.contract ? 'Contrat signé' : 'En attente'}
                    </div>
                  </div>
                  <div className="text-purple-300 text-sm">Manager: {client.manager}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'emails':
        return (
          <div className="relative bg-gradient-to-br from-orange-900 to-red-900 rounded-2xl p-6 h-80">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center mr-3">
                📧
              </div>
              <span className="text-white font-semibold">Templates Email</span>
            </div>
            
            {/* Email templates */}
            <div className="space-y-3">
              {[
                { name: 'Premier Contact', vars: ['{{Name}}', '{{Company}}'] },
                { name: 'Relance Candidat', vars: ['{{Name}}', '{{Position}}'] },
                { name: 'Proposition Poste', vars: ['{{Name}}', '{{Salary}}'] }
              ].map((template, i) => (
                <div key={i} className="bg-orange-800/30 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-white font-medium">{template.name}</div>
                    <button className="px-2 py-1 bg-orange-600/50 text-orange-100 text-xs rounded">
                      Utiliser
                    </button>
                  </div>
                  <div className="flex space-x-2">
                    {template.vars.map((variable, j) => (
                      <span key={j} className="px-2 py-1 bg-orange-600/20 text-orange-200 text-xs rounded">
                        {variable}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Usage indicator */}
            <div className="absolute bottom-4 right-4 text-orange-300 text-sm">
              Variables auto
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section ref={sectionRef} id="next-section" className="py-20 ">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Logiciel tout en un, simple et efficace !
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Tout ce dont vous avez besoin pour gérer, suivre et optimiser vos processus de recrutement dans une plateforme intelligente
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Feature Navigation */}
          <div className={`space-y-6 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            {features.map((feature, index) => (
              <div
                key={feature.id}
                className={`p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                  activeFeature === index
                    ? 'bg-white border-blue-500 shadow-xl scale-105'
                    : 'bg-white/50 border-gray-200 hover:border-gray-300 hover:shadow-lg'
                }`}
                onClick={() => setActiveFeature(index)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${feature.color} text-white text-2xl flex-shrink-0`}>
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 mb-4">{feature.description}</p>
                    
                    {/* Feature Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      {feature.stats.map((stat, i) => (
                        <div key={i} className="text-center">
                          <div className={`text-lg font-bold bg-gradient-to-r ${feature.color} bg-clip-text text-transparent`}>
                            {stat.value}
                          </div>
                          <div className="text-xs text-gray-500">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Feature Mockup */}
          <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full opacity-20 animate-pulse"></div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
              
              {/* Main mockup */}
              <div className="relative z-10">
                {renderMockup(features[activeFeature].mockup)}
              </div>
            </div>
          </div>
        </div>

        {/* Feature Indicators */}
        <div className="flex justify-center mt-12 space-x-3">
          {features.map((_, index) => (
            <button
              key={index}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                activeFeature === index ? 'bg-blue-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
              }`}
              onClick={() => setActiveFeature(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesShowcase;